/**
 * useTrafficAnalytics — one hook per traffic-dashboard tab. Each
 * fetch-on-mount + refetch-on-demand. Kiran 2026-07-02.
 *
 * Kept as thin sibling hooks (not a shared context) so each tab loads
 * on its own click without dragging in the other 9 endpoints. Every
 * hook returns `{ data, isLoading, error, reload }` in the same shape
 * so the page shell can render the loading + error states uniformly.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { parseApiError } from '../utils/api/parseApiError'

interface HookResult<T> {
  readonly data: T | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
}

function makeHook<T>(url: string, defaultQuery = ''): () => HookResult<T> {
  return function useAnalytic(): HookResult<T> {
    const { authedFetch } = useAuth()
    const [data, setData] = useState<T | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | null>(null)
    const [tick, setTick] = useState<number>(0)

    useEffect(() => {
      let cancelled = false
      setIsLoading(true)
      setError(null)
      ;(async () => {
        try {
          const res = await authedFetch(`${url}${defaultQuery}`)
          if (!res.ok) {
            const { message } = await parseApiError(res)
            if (!cancelled) setError(message)
            return
          }
          const body = (await res.json()) as { data: T }
          if (!cancelled) setData(body.data)
        } catch (e) {
          if (!cancelled) setError(e instanceof Error ? e.message : 'Load failed')
        } finally {
          if (!cancelled) setIsLoading(false)
        }
      })()
      return () => { cancelled = true }
    }, [authedFetch, tick])

    const reload = useCallback(() => setTick((t) => t + 1), [])
    return { data, isLoading, error, reload }
  }
}

/* ─────────── Shared types (mirror the repo shapes) ─────────── */

export interface OverviewPayload {
  readonly overview: {
    readonly sessions_today: number
    readonly sessions_yesterday: number
    readonly pageviews_today: number
    readonly signed_in_today: number
    readonly anon_today: number
    readonly bot_share_pct: number
    readonly orders_today: number
    readonly conversion_pct: number
  }
  readonly hourly: ReadonlyArray<{ hour: string; sessions: number; pageviews: number }>
}

export interface AttributionPayload {
  readonly days: number
  readonly referers: ReadonlyArray<{ key: string; count: number }>
  readonly utm: ReadonlyArray<{
    readonly source: string; readonly medium: string; readonly campaign: string
    readonly sessions: number; readonly orders: number; readonly conversion_pct: number
  }>
}

export interface RealtimePayload {
  readonly minutes: number
  readonly visitors: ReadonlyArray<{
    readonly session_id: string; readonly path: string | null
    readonly country: string | null; readonly city: string | null
    readonly device_type: string | null; readonly browser: string | null
    readonly is_authenticated: number; readonly last_seen_at: string
    readonly seconds_ago: number
  }>
}

export interface FunnelPayload {
  readonly days: number
  readonly stages: ReadonlyArray<{ stage: string; sessions: number }>
}

export interface PagesPayload {
  readonly days: number
  readonly top: ReadonlyArray<{ path: string; views: number; avg_dwell_ms: number | null }>
  readonly exits: ReadonlyArray<{ path: string; exits: number }>
}

export interface GeoPayload {
  readonly days: number
  readonly byCountry: ReadonlyArray<{ key: string; count: number }>
  readonly byCity: ReadonlyArray<{ country: string; city: string | null; sessions: number }>
}

export interface DevicesPayload {
  readonly days: number
  readonly devices: ReadonlyArray<{ key: string; count: number }>
  readonly browsers: ReadonlyArray<{ key: string; count: number }>
  readonly operating_systems: ReadonlyArray<{ key: string; count: number }>
}

export interface CohortsPayload {
  readonly weeks: number
  readonly cohorts: ReadonlyArray<{
    readonly cohort_week: string
    readonly new_visitors: number
    readonly returning_visitors: number
  }>
}

export interface FraudPayload {
  readonly days: number
  readonly alerts: ReadonlyArray<{
    readonly session_id: string
    readonly bot_score: number | null
    readonly threat_score: number | null
    readonly as_organization: string | null
    readonly country: string | null
    readonly page_view_count: number
    readonly last_seen_at: string
  }>
}

export interface SessionsPayload {
  readonly days: number
  readonly page: number
  readonly pageSize: number
  readonly total: number
  readonly items: ReadonlyArray<{
    readonly session_id: string
    readonly first_seen_at: string
    readonly last_seen_at: string
    readonly country: string | null
    readonly city: string | null
    readonly device_type: string | null
    readonly browser: string | null
    readonly page_view_count: number
    readonly is_authenticated: number
    readonly customer_id: string | null
    readonly last_page_path: string | null
  }>
}

export interface SessionDetailPayload {
  readonly session: {
    readonly session_id: string
    readonly first_seen_at: string
    readonly last_seen_at: string
    readonly country: string | null
    readonly city: string | null
    readonly device_type: string | null
    readonly browser: string | null
    readonly page_view_count: number
    readonly is_authenticated: number
    readonly customer_id: string | null
    readonly last_page_path: string | null
    readonly os: string | null
    readonly first_referer_host: string | null
    readonly first_utm_source: string | null
    readonly first_utm_campaign: string | null
    readonly exit_page_path: string | null
    readonly as_organization: string | null
    readonly viewport_width: number | null
    readonly viewport_height: number | null
    readonly connection_type: string | null
  }
  readonly pageviews: ReadonlyArray<{
    readonly path: string
    readonly entered_at: string
    readonly dwell_ms: number | null
    readonly is_exit: number
  }>
}

/* ─────────── Per-tab hooks ─────────── */

export const useTrafficOverview     = makeHook<OverviewPayload>(APIROUTES.TRAFFICOVERVIEW)
export const useTrafficAttribution  = makeHook<AttributionPayload>(APIROUTES.TRAFFICATTRIBUTION, '?days=7')
export const useTrafficRealtime     = makeHook<RealtimePayload>(APIROUTES.TRAFFICREALTIME, '?minutes=5')
export const useTrafficFunnel       = makeHook<FunnelPayload>(APIROUTES.TRAFFICFUNNEL, '?days=7')
export const useTrafficPages        = makeHook<PagesPayload>(APIROUTES.TRAFFICPAGES, '?days=7')
export const useTrafficGeo          = makeHook<GeoPayload>(APIROUTES.TRAFFICGEO, '?days=30')
export const useTrafficDevices      = makeHook<DevicesPayload>(APIROUTES.TRAFFICDEVICES, '?days=30')
export const useTrafficCohorts      = makeHook<CohortsPayload>(APIROUTES.TRAFFICCOHORTS, '?days=8')
export const useTrafficFraud        = makeHook<FraudPayload>(APIROUTES.TRAFFICFRAUD, '?days=7')
export const useTrafficSessions     = makeHook<SessionsPayload>(APIROUTES.TRAFFICSESSIONS, '?days=7&page=1&pageSize=25')

/**
 * Session drill-down — takes an id so it can't be a generic makeHook.
 * Returns null data when sessionId is null (page hasn't clicked yet).
 */
export function useTrafficSessionDetail(sessionId: string | null): HookResult<SessionDetailPayload> {
  const { authedFetch } = useAuth()
  const [data, setData] = useState<SessionDetailPayload | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState<number>(0)

  useEffect(() => {
    if (sessionId === null) {
      setData(null)
      setIsLoading(false)
      setError(null)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        const res = await authedFetch(fillPath(APIROUTES.TRAFFICSESSIONDETAIL, { sessionId }))
        if (!res.ok) {
          const { message } = await parseApiError(res)
          if (!cancelled) setError(message)
          return
        }
        const body = (await res.json()) as { data: SessionDetailPayload }
        if (!cancelled) setData(body.data)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Load failed')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [authedFetch, sessionId, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, isLoading, error, reload }
}
